import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertTriangle, Filter, Play, Coffee, Bed, Wrench, MapPin, HelpCircle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface Vehicle { id: string; plateEn: string | null; code: string | null }

interface DailyWaste {
  date: string;
  totalIdleMinutes: number;
  productiveIdleMinutes: number;
  wastedIdleMinutes: number;
  wastedLiters: number;
  wasteCostSar: number;
  sessionCount: number;
  breakdown: Record<string, { sessions: number; minutes: number }>;
}

interface IdleEvent {
  id: string;
  started_at: string;
  ended_at: string;
  duration_minutes: string;
  lat: string | null;
  lng: string | null;
  nearest_site_id: string | null;
  nearest_site_meters: number | null;
  classification: string;
  productive: boolean;
}

interface LeaderRow {
  vehicle_id: string;
  plate_en: string | null;
  code: string | null;
  days: number;
  total_waste_sar: string;
  total_wasted_minutes: string;
}

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMin = (n: number) => Math.round(n) + ' min';

const CLASS_META: Record<string, { label: string; icon: React.ElementType; tone: string }> = {
  loading:   { label: 'Loading',   icon: MapPin,      tone: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' },
  lunch:     { label: 'Lunch',     icon: Coffee,      tone: 'bg-amber-500/10 text-amber-700 dark:text-amber-400' },
  breakdown: { label: 'Breakdown', icon: Wrench,      tone: 'bg-rose-500/10 text-rose-700 dark:text-rose-400' },
  sleep:     { label: 'Sleep',     icon: Bed,         tone: 'bg-indigo-500/10 text-indigo-700 dark:text-indigo-400' },
  unknown:   { label: 'Unknown',   icon: HelpCircle,  tone: 'bg-muted text-muted-foreground' },
};

function defaultRange() {
  const now = new Date();
  const to = now.toISOString().slice(0, 10);
  const from = new Date(now.getTime() - 14 * 86400000).toISOString().slice(0, 10);
  return { from, to };
}

export default function IdleWastePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  const def = useMemo(defaultRange, []);
  const [from, setFrom] = useState(def.from);
  const [to, setTo] = useState(def.to);
  const [vehicleId, setVehicleId] = useState<string>('');
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [days, setDays] = useState<DailyWaste[]>([]);
  const [leader, setLeader] = useState<LeaderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [openDay, setOpenDay] = useState<{ vehicleId: string; date: string } | null>(null);
  const [dayEvents, setDayEvents] = useState<IdleEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);

  // Load vehicles once
  useEffect(() => {
    apiClient.get<Vehicle[]>('/api/v1/vehicles?status=active&pageSize=200')
      .then((r) => setVehicles(Array.isArray(r.data) ? r.data : (r.data as { items?: Vehicle[] }).items ?? []))
      .catch(() => setVehicles([]));
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const calls: Promise<unknown>[] = [];
      if (vehicleId) {
        calls.push(apiClient.get<DailyWaste[]>('/api/v1/idle-detection/vehicles/' + vehicleId + '/idle-waste?from=' + from + '&to=' + to));
      } else {
        calls.push(Promise.resolve({ data: [] }));
      }
      calls.push(apiClient.get<LeaderRow[]>('/api/v1/idle-detection/idle-waste/leaderboard?period=last30&limit=10'));
      const [r1, r2] = await Promise.all(calls) as [{ data: DailyWaste[] }, { data: LeaderRow[] }];
      setDays(r1.data ?? []);
      setLeader(r2.data ?? []);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [from, to, vehicleId]);

  useEffect(() => { refresh(); }, [refresh]);

  async function runDetection() {
    setRunning(true);
    try {
      await apiClient.post('/api/v1/idle-detection/idle-waste/run', { vehicleId: vehicleId || undefined, from, to });
      await refresh();
    } catch (err) {
      window.alert('Detection run failed: ' + extractErrorMessage(err));
    } finally {
      setRunning(false);
    }
  }

  async function openEvents(vId: string, date: string) {
    setOpenDay({ vehicleId: vId, date });
    setEventsLoading(true);
    try {
      const r = await apiClient.get<IdleEvent[]>('/api/v1/idle-detection/vehicles/' + vId + '/idle-events?date=' + date);
      setDayEvents(r.data);
    } catch {
      setDayEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }

  const totalWasteThisRange = days.reduce((s, d) => s + d.wasteCostSar, 0);
  const worstDay = days.reduce<DailyWaste | null>((worst, d) => (worst === null || d.wasteCostSar > worst.wasteCostSar) ? d : worst, null);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button>
          </Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Clock className="h-4 w-4" /> Idle waste</div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-4 space-y-4">
        {/* Filters + run-now */}
        <div className="rounded-lg border border-border p-3 flex flex-wrap items-end gap-3">
          <Filter className="h-4 w-4 text-muted-foreground mt-2" />
          <div className="space-y-1">
            <Label htmlFor="from">From</Label>
            <Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" />
          </div>
          <div className="space-y-1">
            <Label htmlFor="to">To</Label>
            <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" />
          </div>
          <div className="space-y-1 flex-1 min-w-[14rem]">
            <Label htmlFor="vehicle">Vehicle</Label>
            <select
              id="vehicle"
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base"
            >
              <option value="">— Select to view per-day —</option>
              {vehicles.map((v) => (<option key={v.id} value={v.id}>{v.plateEn ?? v.code ?? v.id.slice(0, 8)}</option>))}
            </select>
          </div>
          {isAdmin && (
            <Button onClick={runDetection} disabled={running} className="gap-2">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Run detection
            </Button>
          )}
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <SummaryCard label="Range waste" value={fmtSar(totalWasteThisRange)} tone={totalWasteThisRange > 0 ? 'bad' : undefined} />
          <SummaryCard label="Days analyzed" value={String(days.length)} />
          <SummaryCard label="Worst day" value={worstDay ? worstDay.date.slice(0, 10) + ' · ' + fmtSar(worstDay.wasteCostSar) : '—'} />
        </div>

        {/* Top wasters across fleet */}
        <div className="rounded-lg border border-border">
          <div className="px-4 py-2 bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground inline-flex items-center gap-2 w-full">
            Worst vehicles · last 30d
          </div>
          {leader.length === 0 ? (
            <div className="p-4 text-xs text-muted-foreground">No waste recorded yet. Run detection above to populate.</div>
          ) : (
            <div className="divide-y divide-border">
              {leader.map((r, i) => (
                <div key={r.vehicle_id} className="px-4 py-2 flex items-center justify-between text-sm">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-muted-foreground w-5">{i + 1}.</span>
                    <span className="font-mono">{r.plate_en ?? r.code ?? r.vehicle_id.slice(0, 8)}</span>
                    <span className="text-xs text-muted-foreground">{r.days} days</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-muted-foreground">{fmtMin(Number(r.total_wasted_minutes))}</span>
                    <span className="font-mono">{fmtSar(Number(r.total_waste_sar))}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Per-day table for selected vehicle */}
        <div className="rounded-lg border border-border overflow-hidden">
          <div className="px-4 py-2 bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground flex items-center justify-between">
            <span>{vehicleId ? 'Per-day waste' : 'Pick a vehicle to see day-by-day'}</span>
            <span>{days.length} days</span>
          </div>
          {!vehicleId ? (
            <div className="p-8 text-sm text-muted-foreground text-center">Select a vehicle above.</div>
          ) : loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : error ? (
            <div className="p-4 text-rose-600 dark:text-rose-400 inline-flex items-center gap-2 text-sm"><AlertTriangle className="h-4 w-4" /> {error}</div>
          ) : days.length === 0 ? (
            <div className="p-8 text-sm text-muted-foreground text-center">No waste data for this period. Run detection above.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-xs text-muted-foreground bg-background border-b border-border">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-right px-3 py-2">Sessions</th>
                    <th className="text-right px-3 py-2">Total idle</th>
                    <th className="text-right px-3 py-2">Productive</th>
                    <th className="text-right px-3 py-2">Wasted</th>
                    <th className="text-right px-3 py-2">Liters</th>
                    <th className="text-right px-3 py-2">Cost</th>
                    <th className="text-left px-3 py-2">Pattern</th>
                  </tr>
                </thead>
                <tbody>
                  {days.map((d) => {
                    const top = topPattern(d.breakdown);
                    return (
                      <tr key={d.date} className="border-b border-border last:border-b-0 hover:bg-muted/30 cursor-pointer" onClick={() => openEvents(vehicleId, d.date.slice(0, 10))}>
                        <td className="px-3 py-2 font-mono">{new Date(d.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</td>
                        <td className="px-3 py-2 text-right">{d.sessionCount}</td>
                        <td className="px-3 py-2 text-right">{fmtMin(d.totalIdleMinutes)}</td>
                        <td className="px-3 py-2 text-right text-emerald-700 dark:text-emerald-400">{fmtMin(d.productiveIdleMinutes)}</td>
                        <td className="px-3 py-2 text-right text-amber-700 dark:text-amber-400">{fmtMin(d.wastedIdleMinutes)}</td>
                        <td className="px-3 py-2 text-right">{d.wastedLiters.toFixed(1)} L</td>
                        <td className="px-3 py-2 text-right font-mono">{d.wasteCostSar > 0 ? fmtSar(d.wasteCostSar) : '—'}</td>
                        <td className="px-3 py-2"><PatternBadge name={top} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {/* Day-detail modal */}
      {openDay && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setOpenDay(null)}>
          <div className="bg-background rounded-lg max-w-3xl w-full max-h-[85vh] overflow-y-auto border border-border" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div className="text-sm font-medium">Idle sessions — {openDay.date}</div>
              <Button variant="ghost" size="sm" onClick={() => setOpenDay(null)}>Close</Button>
            </div>
            <div className="p-4">
              {eventsLoading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
              ) : dayEvents.length === 0 ? (
                <div className="text-center text-sm text-muted-foreground py-8">No idle sessions on this day.</div>
              ) : (
                <div className="space-y-2">
                  {dayEvents.map((e) => {
                    const meta = CLASS_META[e.classification] ?? CLASS_META.unknown;
                    const Icon = meta.icon;
                    const start = new Date(e.started_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                    const end   = new Date(e.ended_at  ).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
                    const lat = e.lat ? Number(e.lat) : null;
                    const lng = e.lng ? Number(e.lng) : null;
                    return (
                      <div key={e.id} className="rounded-lg border border-border p-3 flex items-center gap-3 text-sm">
                        <div className={'h-9 w-9 rounded-full flex items-center justify-center ' + meta.tone}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium">{meta.label}{e.productive ? ' · productive' : ''}</div>
                          <div className="text-xs text-muted-foreground font-mono">
                            {start} → {end} · {Math.round(Number(e.duration_minutes))} min
                            {e.nearest_site_meters !== null ? ' · ' + e.nearest_site_meters + 'm from site' : ''}
                          </div>
                        </div>
                        {lat !== null && lng !== null && (
                          <a
                            href={'https://www.google.com/maps/search/?api=1&query=' + lat + ',' + lng}
                            target="_blank" rel="noreferrer"
                            className="text-xs text-blue-500 hover:underline"
                          >
                            map
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function topPattern(breakdown: DailyWaste['breakdown']): string {
  let top: string | null = null;
  let max = 0;
  for (const [k, v] of Object.entries(breakdown ?? {})) {
    if (v.minutes > max) { max = v.minutes; top = k; }
  }
  return top ?? 'unknown';
}

function PatternBadge({ name }: { name: string }) {
  const meta = CLASS_META[name] ?? CLASS_META.unknown;
  const Icon = meta.icon;
  return (
    <span className={'inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ' + meta.tone}>
      <Icon className="h-3 w-3" /> {meta.label}
    </span>
  );
}

function SummaryCard({ label, value, tone }: { label: string; value: string; tone?: 'good' | 'bad' }) {
  const cls = tone === 'bad' ? 'text-rose-600 dark:text-rose-400' : tone === 'good' ? 'text-emerald-600 dark:text-emerald-400' : '';
  return (
    <div className="bg-muted rounded-lg p-4">
      <div className="text-xs text-muted-foreground mb-1 uppercase tracking-wider">{label}</div>
      <div className={'text-2xl font-medium ' + cls}>{value}</div>
    </div>
  );
}
